import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, MessageCircle, User, RefreshCw, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts dialogue data from various backend shapes.
 * @param {Object} lesson
 * @returns {Array|null}
 */
function getDialogues(lesson) {
  const raw =
    lesson?.content?.dialogues ||
    lesson?.dialogues ||
    lesson?.content?.steps ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((d, i) => ({
      id: d.id || i + 1,
      speaker: d.speaker || (d.isUser ? 'You' : 'Character'),
      text: d.text || d.content || '',
      isUser: d.isUser || d.is_user || false,
      options: d.options
        ? d.options.map(o => ({
            text: o.text || o.label || '',
            isCorrect: o.isCorrect || o.is_correct || false,
            feedback: o.feedback || '',
          }))
        : undefined,
    }));
  }
  return null;
}

/**
 * DialogueLesson — Senaryo bazli diyalog alıştırması (lesson_type === 'dialogue').
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.dialogues: [{ speaker, text, isUser, options?: [{ text, isCorrect, feedback }] }]
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function DialogueLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawDialogues = getDialogues(lesson);
  const hasContent = rawDialogues !== null && rawDialogues.length > 0;
  const dialogues = hasContent ? rawDialogues : [];

  const sceneTitle = lesson?.content?.scene_title || lesson?.scene_title || null;

  const [step, setStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Count how many user turns exist
  const userTurns = dialogues.filter(d => d.isUser && d.options?.length > 0).length;

  const current = dialogues[step];

  const handleOptionSelect = useCallback((optIdx) => {
    if (selectedOption !== null) return; // already answered
    const option = current.options[optIdx];
    setSelectedOption(optIdx);
    setFeedback({ text: option.feedback, isCorrect: option.isCorrect });
    if (option.isCorrect) {
      setCorrectCount(c => c + 1);
    }
  }, [current, selectedOption]);

  const handleNext = useCallback(() => {
    setSelectedOption(null);
    setFeedback(null);
    if (step + 1 >= dialogues.length) {
      setFinished(true);
    } else {
      setStep(s => s + 1);
    }
  }, [step, dialogues.length]);

  const handleSkipNonUser = useCallback(() => {
    if (step + 1 >= dialogues.length) {
      setFinished(true);
    } else {
      setStep(s => s + 1);
    }
  }, [step, dialogues.length]);

  const restart = () => {
    setStep(0);
    setSelectedOption(null);
    setFeedback(null);
    setCorrectCount(0);
    setFinished(false);
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const score = userTurns > 0 ? Math.round((correctCount / userTurns) * 100) : 100;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="dialogue-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="dialogue-empty-state">
        <MessageCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz diyalog içeriği tanımlanmamış.' : 'No dialogue content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onComplete}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all"
          data-testid="dialogue-empty-complete-btn"
        >
          {t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="dialogue-lesson">
      {/* Finished screen */}
      <AnimatePresence mode="wait">
        {finished ? (
          <motion.div
            key="dialogue-done"
            initial={reduced ? {} : { opacity: 0, scale: 0.92 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 p-8 text-center"
            data-testid="dialogue-completed"
          >
            <div className="w-20 h-20 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-cyan-700 mb-1">
              {t('dialogueLesson.completed')}
            </h2>
            <p className="text-gray-600 font-semibold mb-6">
              {correctCount}/{userTurns} {t('dialogueLesson.correctAnswers')} — {t('dialogueLesson.score')}: %{userTurns > 0 ? Math.round((correctCount / userTurns) * 100) : 100}
            </p>
            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="dialogue-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={restart}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="dialogue-restart-btn"
              >
                <RefreshCw size={16} /> {t('dialogueLesson.restart')}
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={`dialogue-step-${step}`}
            initial={reduced ? {} : { opacity: 0, x: 20 }}
            animate={reduced ? {} : { opacity: 1, x: 0 }}
            exit={reduced ? {} : { opacity: 0, x: -20 }}
            transition={reduced ? {} : { duration: 0.25 }}
          >
            {/* Scene header */}
            {sceneTitle && (
              <div className="rounded-xl border-2 border-cyan-100 bg-cyan-50 px-4 py-3 mb-4 text-sm font-semibold text-cyan-700 flex items-center gap-2">
                <MessageCircle size={16} className="flex-shrink-0" />
                {sceneTitle}
              </div>
            )}

            {/* Progress */}
            <div className="flex justify-between text-xs font-bold text-gray-400 mb-3">
              <span>{t('dialogueLesson.step')} {step + 1}/{dialogues.length}</span>
              {userTurns > 0 && (
                <span className="text-emerald-600">{correctCount}/{userTurns} {t('dialogueLesson.correctAnswers')}</span>
              )}
            </div>

            {/* Dialogue bubble */}
            <DialogueBubble
              dialogue={current}
              selectedOption={selectedOption}
              feedback={feedback}
              reduced={reduced}
              t={t}
              lang={lang}
              onOptionSelect={handleOptionSelect}
            />

            {/* Continue/Next button */}
            {!current.isUser && (
              <div className="mt-4 flex justify-end">
                <motion.button
                  whileHover={reduced ? {} : { scale: 1.03 }}
                  whileTap={reduced ? {} : { scale: 0.97 }}
                  onClick={handleSkipNonUser}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-cyan-400 bg-cyan-500 text-white font-bold shadow-[0_4px_0_#0891b2] active:shadow-none active:translate-y-1 transition-all"
                  data-testid="dialogue-continue-btn"
                >
                  {t('dialogueLesson.continue')} <ChevronRight size={18} />
                </motion.button>
              </div>
            )}

            {/* After user answered: show Next */}
            {current.isUser && feedback !== null && (
              <div className="mt-4 flex justify-end">
                <motion.button
                  whileHover={reduced ? {} : { scale: 1.03 }}
                  whileTap={reduced ? {} : { scale: 0.97 }}
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-cyan-400 bg-cyan-500 text-white font-bold shadow-[0_4px_0_#0891b2] active:shadow-none active:translate-y-1 transition-all"
                  data-testid="dialogue-next-btn"
                >
                  {t('dialogueLesson.continue')} <ChevronRight size={18} />
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DialogueBubble({ dialogue, selectedOption, feedback, reduced, t, lang: _lang, onOptionSelect }) {
  const isUser = dialogue.isUser;
  const hasOptions = isUser && dialogue.options?.length > 0;

  return (
    <div className={`flex gap-3 mb-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
        isUser
          ? 'border-cyan-400 bg-cyan-100 text-cyan-600'
          : 'border-amber-400 bg-amber-100 text-amber-600'
      }`}>
        {isUser
          ? <User size={18} />
          : <MessageCircle size={18} />
        }
      </div>

      <div className={`flex-1 ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        {/* Speaker label */}
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">
          {isUser ? t('dialogueLesson.yourTurn') : dialogue.speaker}
        </span>

        {/* Non-user text bubble */}
        {!isUser && dialogue.text && (
          <div className="rounded-2xl rounded-tl-none border-2 border-amber-200 bg-amber-50 px-4 py-3 text-gray-800 font-semibold max-w-prose" data-testid="dialogue-character-bubble">
            {dialogue.text}
          </div>
        )}

        {/* User turn: options */}
        {hasOptions && (
          <div className="w-full space-y-2" data-testid="dialogue-options">
            {dialogue.options.map((opt, idx) => {
              const isSelected = selectedOption === idx;
              const isAnswered = selectedOption !== null;
              let btnClass = 'w-full text-left px-4 py-3 rounded-xl border-2 font-semibold transition-all text-sm ';

              if (!isAnswered) {
                btnClass += 'border-cyan-200 bg-white hover:border-cyan-400 hover:bg-cyan-50 text-gray-700 shadow-[0_3px_0_#e0f2fe] active:shadow-none active:translate-y-0.5 cursor-pointer';
              } else if (isSelected && opt.isCorrect) {
                btnClass += 'border-emerald-400 bg-emerald-50 text-emerald-700';
              } else if (isSelected && !opt.isCorrect) {
                btnClass += 'border-rose-400 bg-rose-50 text-rose-700';
              } else if (!isSelected && opt.isCorrect) {
                btnClass += 'border-emerald-300 bg-emerald-50 text-emerald-600 opacity-70';
              } else {
                btnClass += 'border-gray-200 bg-gray-50 text-gray-400 opacity-50';
              }

              return (
                <motion.button
                  key={idx}
                  whileHover={(!isAnswered && !reduced) ? { scale: 1.01 } : {}}
                  whileTap={(!isAnswered && !reduced) ? { scale: 0.99 } : {}}
                  onClick={() => onOptionSelect(idx)}
                  disabled={isAnswered}
                  className={btnClass}
                  data-testid={`dialogue-option-${idx}`}
                >
                  <span className="flex items-center gap-2">
                    {isAnswered && isSelected && (
                      opt.isCorrect
                        ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                        : <XCircle size={16} className="text-rose-500 flex-shrink-0" />
                    )}
                    {isAnswered && !isSelected && opt.isCorrect && (
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
                    )}
                    {opt.text}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Feedback banner */}
        {feedback && (
          <AnimatePresence>
            <motion.div
              initial={reduced ? {} : { opacity: 0, y: 8 }}
              animate={reduced ? {} : { opacity: 1, y: 0 }}
              className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold w-full ${
                feedback.isCorrect
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-rose-300 bg-rose-50 text-rose-700'
              }`}
              data-testid="dialogue-feedback"
            >
              <span className="font-bold mr-1">
                {feedback.isCorrect ? t('dialogueLesson.correct') : t('dialogueLesson.incorrect')}
              </span>
              {feedback.text}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
